import {
  Component,
  Host,
  h,
  Element,
  Prop,
  State,
  Watch,
  Event,
  EventEmitter,
  Listen,
  Method,
} from '@stencil/core';
import classcat from 'classcat';
import './aha-dialog-window-events';

import { Positions, Directions, directions } from './types';

import * as Common from './dialog-common';

export interface AhaDialog extends HTMLAhaDialogElement {}

/** `<aha-dialog>` is an enhanced native `<dialog>` component. It serves to add a few additional features:
 *
 * 1. By default opens the dialog in "modal" mode, rather than "inline". This ensures that it is rendered on top of all other content on the page, and breaks out of any z-index/overflow rendering context.
 * 2. Makes it possible to open the dialog in "modal" mode using just attributes, rather than a separate JS function call.
 * 3. Makes it possible to add the dialog to a page in an "open" state, without further effort, simply by adding an "open" attribute.
 * 4. Makes it so that events are emitted before and after both opening and closing the dialog. (before open, after open, before close, after close).
 * 5. Makes it so that only the top-most dialog will have a backdrop.
 * 6. Can restrict scrolling to the top-most dialog, and prevents scrolling anywhere else on the screen (see the `restrict` attribute below).
 *
 * By and large you will likely _not_ be using `<aha-dialog>` directly (unless you need a basic `<dialog>` component) ... rather it is likely that you will be using the basic methods and props here as a building block for other components. eg. `aha-menu`, `aha-tooltip`, etc.
 *
 * ## Public methods
 *
 * `toggle()`
 * -- Use this method to toggle the open/closed state of the dialog. If the dialog is open, it will close it. If the dialog is closed, it will open it.
 *
 * `show()`
 * -- Use this method to open the dialog. If the dialog is already open, this method will do nothing.
 *
 * `hide()`
 * -- Use this method to close the dialog. If the dialog is already closed, this method will do nothing.
 *
 * `bringToFront()`
 * -- Use this method to bring the dialog to the front of the stacking context. This is useful if you have multiple dialogs open, and you want to make sure that the dialog you are working with is on top of all the others.
 *
 * `reposition()`
 *  -- Use if the `<aha-dialog>` is positioned relative to some other element and you need to reposition the `<aha-dialog>` to be in the correct place on the screen, relative to that element. You likely will never need to call this method, however if you do, it is there for your use.
 *
 * ## Development notes
 * When working with the native <dialog> element and when the dialog is open, it has a few curious properties:
 *
 * 1. It sits on top of all other elements on the page, in its own stacking context. There is no way to make clicking "bleed through" to the other elements in the lower stacking context.
 * 2. When getting `document.elementsFromPoint(x,y)` it will return only the top-most open dialog, and the root <HTML> element, nothing else.
 * 3. To find what elements are found at point `x,y`, you effectively have to close the top-most dialog, and then calculate the other elements underneath it. You may need to do this several times if you have multiple open dialogs.
 *
 */
@Component({
  tag: 'aha-dialog',
  styleUrl: 'aha-dialog.css',
  shadow: true,
})
export class AhaDialog {
  @Element() host!: HTMLAhaDialogElement;

  // ================== Content that should be shared with anything that would normally subclass AhaDialog. Effectively all of the code ... thanks Stencil! ================== //

  // Reference to the native <dialog> element.
  protected dialogElem!: HTMLDialogElement;

  // Reference to the inner "contents" area of the native <dialog> element.
  protected innerElem?: HTMLElement;

  // The element which a relatively positioned `<aha-dialog>` will position itself around. Note that this is only set at specific instances in the component's lifecycle, typically when first attached to a page, and any time the `position` property is changed from some value to `relative`.
  public positionTarget?: HTMLElement;

  // Scroll event handler function.
  protected scrollEvent?: any;

  // The styles that exist on the <aha-dialog> immediately before the dialog is opened. This gives us a good "reset" style to return to when the <aha-dialog> is closed.
  protected stylesBeforeOpened: string = '';

  // Various states to control open/close animations.
  @State() closing?: boolean;
  @State() opening?: boolean;

  /** Whether or not an `<aha-dialog>` element has been cancelled by pressing the escape key. **(Read only)** */
  @Prop() cancelled?: boolean;

  /** When `persistent` is true, two things happen:
   *
   * 1. the `<aha-dialog>` component cannot be dismissed by pressing the "Escape" key
   * 2. if the `hideBackdrop` option is set to true, the `<aha-dialog>` component will not be closed automatically when clicked outside of the visible area
   *
   * Effectively this means that if you want to close the `<aha-dialog>` component, you'll need to handle the logic for doing so on your own.
   */
  @Prop({ reflect: true }) persistent?: boolean;

  /** When `hideWhenOutside` is true and if `hideBackdrop` is true, then this `<aha-dialog>` will be hidden when the user moves their pointer outside the "inner" dialog's contents or `positionTarget`.
   */
  @Prop({ reflect: true, attribute: 'hide-when-outside' }) hideWhenOutside?: boolean;

  /** The amount a relatively positioned `<aha-dialog>` element should be offset from its triggering element, relative to its opened direction. If the `position` attribute is not set to "relative", this setting will have no effect. Accepts a value of:
   *
   * * `undefined` (which will place the `<aha-dialog>` just outside the triggering element)
   * * the string `inset` (which will place the `<aha-dialog>` "inside" the boundaries of the triggering element)
   * * any numerical value, eg, `5` or `10` or `20` (which will place the `<aha-dialog>` outside the triggering element by that `n` amount of pixels)
   */
  @Prop({ reflect: true }) offset?: string | number;

  /** Whether or not the `<aha-dialog>` element is open on the page. */
  @Prop({ reflect: true }) open?: boolean;
  @Watch('open')
  protected handleOpen() {
    Common.handleOpen.call(this);
  }

  /** Whether or not the `<aha-dialog>` element has a visible backdrop when opened. When hidden, the backdrop will never show.
   *
   * By default the top-most open `<aha-dialog>` always has a backdrop, unless the attribute `hide-backdrop` is present.
   *
   * The backdrop can be styled directly by targeting the `aha-dialog::part(dialog)::backdrop` CSS selector. */
  @Prop({ reflect: true, attribute: 'hide-backdrop' }) hideBackdrop?: boolean;
  @Watch('hideBackdrop')
  private updateBackdrops() {
    Common.updateBackdrops.call(this);
  }

  /** The positioning strategy of the opened dialog.
   *
   * If the value is set to "relative", see the documentation for the `rel` attribute for further details.
   */
  @Prop({ reflect: true }) position: Positions = 'center';
  @Watch('position')
  private handlePositionChange() {
    Common.handlePositionChange.call(this);
  }

  /** The element that the opened dialog will be positioned relative to. The value can be one of four values:
   *
   * * `undefined`
   * * an HTML element (set via JS)
   * * a string which represents a CSS selector
   * * a string representing a pair of `x,y` coordinates
   *
   * ##### When: The relative element is `undefined`
   * The relative element is `undefined`, and so the element which the `<aha-dialog>` is set relative to is the immediately preceeding sibling element.
   *
   * ```<aha-dialog position="relative"></aha-dialog>```
   *
   * ##### When: The relative element is set to an HTML element
   * The relative element is set to a real DOM node via javascript.
   *
   * ```
   * dialogElem.rel = document.querySelector('button');
   * ```
   *
   * ##### When: The relative element is set via CSS selector
   * The relative element will be the first element on the page that matches the CSS selector.
   *
   * ```
   * <aha-dialog position="relative" rel="#foo"></aha-dialog>
   * ```
   * The code above will find the element on the page with the id of "foo" and position the dialog relative to that.
   *
   * It is also possible to use a slightly expanded CSS syntax to select the closest parent element that matches a CSS selector by using our custom `::closest()` pseudo-element.
   * This selector will find the _closest parent element_ matching the `::closest()` pseudo element and then match any remaining CSS selector based on that closest element.
   *
   * ```
   * <aha-dialog position="relative" rel="::closest(.bar) .baz"></aha-dialog>
   * ```
   *
   * This selector will find the closest parent element with the class of "bar" then position the dialog relative to the position of the ".baz" element inside that.
   *
   * ##### When: The `rel` value is a set to a string of `x,y` coordinates
   * The `<aha-dialog>` will be positioned relative to this point on the page.
   *
   * ```
   * <aha-dialog position="relative" rel="300,400"></aha-dialog>
   * ```
   *
   */
  @Prop({ reflect: true }) rel: undefined | HTMLElement | string;
  @Watch('rel')
  private willReposition() {
    this.reposition();
  }

  /** The direction(s) in which a relatively positioned dialog can be opened. Can accept either a single value (ie, `bottom-start`) or a comma separated list of values (ie, `bottom-start,right,top-start`). If the `position` attribute is not set to "relative", this setting will have no effect. */
  @Prop({ reflect: true }) directions?: Directions;
  @Watch('directions')
  private sanitizeDirections(value: string, oldValue: string, attr: string) {
    Common.sanitizeDirections.call(this, value, oldValue, attr);
  }

  /** The direction in which the dialog is current opened. Only applicable when used with `relative` positioning strategy. */
  @State() openedDirection?: string;

  /** This determines whether or the area outside the `<aha-dialog>` can be scrolled while the dialog is open. */
  @Prop({ reflect: true }) restrict?: boolean;
  @Watch('restrict')
  private handleRestrict() {
    Common.handleRestrict.call(this);
  }

  /** Whether or not the `<aha-dialog>` is currently in a "loading" state. When set to true, a spinner will appear and any contents of the dialog cannot be interacted with until the loading attribute is set to false. */
  @Prop({ reflect: true }) loading?: boolean;

  /** Whether or not this particual dialog is the top-most dialog of all the <aha-dialog> elements. **(Read only)** */
  @Prop() maximal: boolean = false;

  /**
   * Triggers immediately before the `<aha-dialog>` opens.
   */
  @Event({
    eventName: 'aha-dialog:open',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  /**
   * Triggers immediately after the `<aha-dialog>` opens.
   */
  @Event({
    eventName: 'aha-dialog:opened',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers immediately before the `<aha-dialog>` closes.
   */
  @Event({
    eventName: 'aha-dialog:close',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers immediately after the `<aha-dialog>` closes.
   */
  @Event({
    eventName: 'aha-dialog:closed',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  /**
   * Triggers when the "Escape" key is pressed and before the `<aha-dialog>` is closed. Fires unless the `persistent` attribute is set.
   */
  @Event({
    eventName: 'aha-dialog:cancel',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  /** A method by which the `<aha-dialog>`'s `cancel` event can manually be fired. Typically only used when working with elements which wrap an `<aha-dialog>`. */
  @Method()
  async emitCancelEvent() {
    Common.emitCancelEvent.call(this);
  }

  /**
   * Triggers when the "Escape" key is pressed and after the `<aha-dialog>` is closed. Fires unless the `persistent` attribute is set.
   */
  @Event({
    eventName: 'aha-dialog:cancelled',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelledEvent!: EventEmitter;

  /**
   * Triggers when the user mouses outside of both the "inner" contents of the `<aha-dialog>` and any `positionTarget` element.
   */
  @Event({
    eventName: 'aha-dialog:pointer-outside',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  pointerOutsideEvent!: EventEmitter;

  /**
   * Triggers when the user mouses inside either the "inner" contents of the `<aha-dialog>` or any `positionTarget` element.
   */
  @Event({
    eventName: 'aha-dialog:pointer-inside',
    cancelable: true,
    composed: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  pointerInsideEvent!: EventEmitter;

  /** Toggles the `open` state of the dialog. */
  @Method()
  async toggle() {
    Common.toggle.call(this);
  }

  /** Shows the dialog. */
  @Method()
  async show() {
    Common.show.call(this);
  }

  /** Hides the dialog. */
  @Method()
  async hide(immediate?: boolean) {
    Common.hide.call(this, immediate);
  }

  /** Bring the specified dialog to the top-most rendering layer, if it is not already there. Because native <dialog> elements do not have a concept of user manipulatable stacking order (z-index) the only way to force one on top is to close it and then immediately reopen it. */
  @Method()
  async bringToFront() {
    Common.bringToFront.call(this);
  }

  /** Handle canceling the dialog (closing it via "Escape"). */
  @Listen('cancel')
  private handleCancel(e: Event) {
    Common.handleCancel.call(this, e);
  }

  /** When this particular `<aha-dialog>` element is closed, then reset all the styles to give us a good starting point for re-opening. */
  @Listen('aha-dialog:closed')
  private clearInnerStyles(e: CustomEvent) {
    Common.clearInnerStyles.call(this, e);
  }

  /** Event that fires when opening/closing animations are complete. */
  protected handleAnimationEnd(event: AnimationEvent) {
    Common.handleAnimationEnd.call(this, event);
  }

  /** Event that fires when user clicks within the native <dialog> element. */
  protected handleDialogClick(event: MouseEvent) {
    Common.handleDialogClick.call(this, event);
  }

  /** Event that fires when mouses within the native <dialog> element. */
  protected handleDialogPointerMove(event: PointerEvent) {
    Common.handleDialogPointerMove.call(this, event);
  }

  connectedCallback() {
    Common.connectedCallback.call(this);
  }

  componentDidLoad() {
    Common.componentDidLoad.call(this);
  }

  disconnectedCallback() {
    Common.disconnectedCallback.call(this);
  }

  /** Reposition the `<aha-dialog>` relative to the position of it's `rel` element. */
  @Method()
  async reposition(ignoredDirections: Set<string> = new Set()) {
    Common.reposition.call(this, ignoredDirections);
  }

  get isRelativelyPositioned() {
    return Common.isRelativelyPositioned.call(this);
  }

  protected possibleDirections: Set<string> = new Set();

  render() {
    return (
      <Host role="dialog" aria-modal="true" maximal={this.maximal} direction={this.openedDirection}>
        <dialog
          ref={el => (this.dialogElem = el as HTMLDialogElement)}
          // @ts-ignore - onCancel is not a typescript recognized event, even though it is native to the <dialog> element.
          onCancel={this.handleCancel}
          // @ts-ignore
          onClick={this.handleDialogClick}
          onPointerMove={this.handleDialogPointerMove}
          tabIndex={-1}
        >
          <div
            id="inner"
            class={classcat({ opening: this.opening, closing: this.closing })}
            part="dialog"
            ref={el => (this.innerElem = el)}
            onAnimationEnd={this.handleAnimationEnd}
          >
            <slot></slot>
            <div id="loader">
              <aha-spinner size="6ex" opacity={0.8}></aha-spinner>
            </div>
          </div>
        </dialog>
      </Host>
    );
  }
}
